import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import escapeRegExp from 'lodash/escapeRegExp';

import {
  DDP_Code,
  EAP_BH_PSEUDO_ROLLUP_CODE,
  VIRTUAL_BH_PSEUDO_ROLLUP_CODE,
  ddpPseudoCode,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { usePortalShowEAPfilter } from '../../../../hooks/usePortalShowEAPfilter';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { isMemberEAPEligible } from '../../../../utils/user.utils';
import { phoneOnly } from '../../../ConstantsStyles';
import { DisplayCoverageTypes } from './DisplayCoverageTypes';
import {
  SuggestionListItem,
  SuggestionSectionItem,
  isPrimaryActiveSuggestion,
  keywordSearchDropdownIconColor,
} from './KeywordSearchDropdown.styled';

type Props = {
  activeSuggestion: number;
  keywordSearchTerm: string;
  getCoverageType: Array<string>;
  memberDDP: string;
  onClick: any;
  index: number;
  value: any;
  hideDisabledTypeahead: boolean;
};
export const SuggestionItem = ({
  activeSuggestion,
  hideDisabledTypeahead,
  keywordSearchTerm,
  getCoverageType,
  memberDDP,
  onClick,
  value,
  index,
}: Props) => {
  const escapedKeywordSearchTerm = escapeRegExp(keywordSearchTerm);
  const isPhone = useMediaQuery(phoneOnly);
  const portalShowEAPfilter = usePortalShowEAPfilter();

  const disableDDP =
    value?.pseudoVerticals == ddpPseudoCode &&
    !memberDDP?.includes(DDP_Code[value?.suggestion?.toLowerCase()]) &&
    memberDDP !== DDP_Code['All'];

  const isBHVirtualCareUnetRailroadSuggestionSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .SUPPRESS_BH_VIRTUAL_CARE_UNET_RR_SUGGESTIONS
  );

  const isVirtualCareMedicalUnetRailRoadSuggestionSuppressed =
    returnSuppressFlag(
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
        .SUPPRESS_MEDICAL_VIRTUAL_CARE_UNET_RR_SUGGESTIONS
    );

  const hasVirtualBHPseudoVertical =
    value?.pseudoVerticals === VIRTUAL_BH_PSEUDO_ROLLUP_CODE[0];

  const isVirtualCareRollUpCodeSuppressed =
    hasVirtualBHPseudoVertical &&
    isBHVirtualCareUnetRailroadSuggestionSuppressed;

  const [
    isTypeaheadBehavioralHealthTagSuppressed,
    /** Suppressing EAP AOE option for non-EAP clients */
    disableEapTypeheadForNonEapClient,
    enableAutocompleteTaggingChanges,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.SUPPRESS_TYPEAHEAD_BH_TAG,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_EAP_TYPEAHEAD_FOR_NON_EAP_CLIENT,
    ConstantsLagoon.FEATURE_FLAGS.AUTOCOMPLETE_TAGGING_CHANGES,
  ]);

  const isEapRollUpCodeSupressed =
    disableEapTypeheadForNonEapClient &&
    !(isMemberEAPEligible() || portalShowEAPfilter) &&
    value?.pseudoVerticals === EAP_BH_PSEUDO_ROLLUP_CODE;

  return (
    <SuggestionSectionItem
      aria-posinset={index + 1}
      className={`${activeSuggestion === index ? 'active' : ''}`}
      id={
        activeSuggestion === index ? 'primary-search-bar-result-suggestion' : ''
      }
      role="option"
    >
      <SuggestionListItem
        className="suggestion-link-span"
        css={
          value?.coverageType.some((coverageCode) =>
            getCoverageType.includes(coverageCode)
          ) &&
          !disableDDP &&
          !isVirtualCareRollUpCodeSuppressed &&
          !isEapRollUpCodeSupressed
            ? {
                '&.abyss-flex-root':
                  activeSuggestion === index ? isPrimaryActiveSuggestion : '',
              }
            : {
                pointerEvents: 'none',
              }
        }
        data-testid="search-input-box-common-suggestion"
        key={value.id}
        onClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          onClick(value);
        }}
        style={{ whiteSpace: isPhone ? '' : 'pre' }}
      >
        <IconSymbol
          color={keywordSearchDropdownIconColor}
          data-testid="search-input-box-common-suggestion-icon"
          icon="search"
        />
        <Flex
          alignItems="center"
          css={{ flexWrap: 'wrap', marginLeft: '0.25rem' }}
        >
          <Text css={{ marginRight: '8px' }} data-testid="search-suggestions">
            {value?.suggestion
              ?.split(new RegExp(`(${escapedKeywordSearchTerm})`, 'gi'))
              .map((part, index) => {
                const isHighlighted =
                  part.toLowerCase() === keywordSearchTerm.toLowerCase();
                const textColor =
                  !value?.coverageType.some((coverageCode) =>
                    getCoverageType.includes(coverageCode)
                  ) ||
                  disableDDP ||
                  isVirtualCareRollUpCodeSuppressed ||
                  isEapRollUpCodeSupressed
                    ? '$core.color.neutral.60'
                    : '$core.color.brand.60';

                return (
                  <Text
                    color={textColor}
                    fontSize="lg"
                    fontWeight={isHighlighted ? undefined : 'bold'}
                    key={`${
                      isHighlighted ? 'highlighted' : 'regular'
                    }-${index}`}
                  >
                    {part}
                  </Text>
                );
              })}
          </Text>
          <DisplayCoverageTypes
            enableAutocompleteTaggingChanges={enableAutocompleteTaggingChanges}
            getCoverageType={getCoverageType}
            hideDisabledTypeahead={hideDisabledTypeahead}
            isEapRollUpCodeSupressed={isEapRollUpCodeSupressed}
            isTypeaheadBehavioralHealthTagSuppressed={
              isTypeaheadBehavioralHealthTagSuppressed
            }
            isVirtualCareMedicalSuppressed={
              isVirtualCareMedicalUnetRailRoadSuggestionSuppressed
            }
            isVirtualCareRollUpCodeSuppressed={
              isVirtualCareRollUpCodeSuppressed
            }
            item={value}
            memberDDP={memberDDP}
          />
        </Flex>
      </SuggestionListItem>
    </SuggestionSectionItem>
  );
};
